import {MarketValue} from '../../domain/entities/B2BDashboard'
import {formatNumber} from '../../utils/formatNumber.ts'

export {formatNumber}

export const sum = (values: number[]): number => values.reduce((acc, v) => acc + v, 0)

export type MarketBreakdownItem = {
    code: string
    widthPct: number
    value: string
}

// Turns the API's { market, value }[] (zero-value markets already omitted by
// the backend) into bar-chart-ready rows, scaled against the largest value.
export const marketBreakdownFromPairs = (
    pairs: MarketValue[],
    formatValue: (v: number) => string = formatNumber
): MarketBreakdownItem[] => {
    if (pairs.length === 0) return []
    const max = Math.max(...pairs.map((p) => p.value)) || 1
    return pairs.map((p) => ({
        code: p.market,
        widthPct: Math.max(2, Math.round((p.value / max) * 100)),
        value: formatValue(p.value)
    }))
}

// Gelato stage times come in hours; switch to days once the value is big
// enough that hours stop being a readable unit.
export const formatStageDuration = (hours: number | null): { value: string; unit: string } => {
    if (hours == null) return { value: '—', unit: '' }
    if (hours < 24) return { value: formatNumber(hours), unit: 'h' }
    const days = Math.round((hours / 24) * 10) / 10
    return { value: days.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: 'always' } as any), unit: 'días' }
}

// Rounds a rough step up to a "nice" 1/2/5 * 10^n value, so axis ticks read as
// round numbers (e.g. 2000, 500, 25) instead of the raw peak/4.
const niceStep = (roughStep: number): number => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep || 1)))
    const residual = roughStep / magnitude
    const niceResidual = residual > 5 ? 10 : residual > 2 ? 5 : residual > 1 ? 2 : 1
    return niceResidual * magnitude
}

export type YAxis = { ceiling: number; ticks: string[] }

// Builds 5 evenly-spaced Y-axis ticks (ceiling down to 0) for a bar/line chart,
// where `ceiling` is a "nice" round number at or above `maxValue` — use it as
// the scale denominator for bar heights so they line up with the printed ticks.
export const buildYAxis = (maxValue: number, formatValue: (v: number) => string = formatNumber): YAxis => {
    const step = niceStep(Math.max(1, maxValue) / 4)
    const ceiling = step * 4
    return { ceiling, ticks: [4, 3, 2, 1, 0].map((i) => formatValue(step * i)) }
}

// Portfolio health byMarket values are fractions (0..1) that already read as a
// percentage on their own — unlike marketBreakdownFromPairs, the bar width is
// the value itself, not scaled against the largest market.
export const portfolioMarketBreakdown = (pairs: MarketValue[]): MarketBreakdownItem[] =>
    pairs.map((p) => ({
        code: p.market,
        widthPct: Math.max(2, Math.round(p.value * 100)),
        value: `${Math.round(p.value * 100)}%`
    }))
